 import React, { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout/Layout";
import axios from "axios";
import getUserData from "../utils/userDataService.js";
import { saveRecentProduct } from "../utils/recentProducts";
import "./ProductInfo.css";

/* =========================
 * Config
 * ========================= */
const ORDER_CREATE_ENDPOINT = "/api/LOL/create";
const RECENT_DETAILS_LIMIT = 5;

/* =========================
 * Helpers
 * ========================= */
function normalizePath(p = "") {
  return String(p || "").replace(/\\/g, "/").replace(/^\/+/, "");
}

function assetUrl(p = "") {
  const raw = String(p || "").trim();
  if (!raw) return "";

  if (/^https?:\/\//i.test(raw)) return raw;

  const v = raw.replace(/\\/g, "/");

  if (v.includes(":/")) {
    const filename = v.split("/").pop();
    const lower = v.toLowerCase();

    if (lower.includes("/productimages/")) return `/productImages/${filename}`;
    if (lower.includes("/banners/")) return `/banners/${filename}`;
    if (lower.includes("/gallery/")) return `/gallery/${filename}`;
    if (lower.includes("/categoryimages/")) return `/categoryImages/${filename}`;

    return `/productImages/${filename}`;
  }

  if (v.startsWith("/")) return v;
  return "/" + normalizePath(v);
}

function prettyCat(s = "") {
  return String(s || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toUpperCase();
}

function toMoneyNumber(v) {
  const n = typeof v === "number" ? v : Number(String(v || "").trim());
  return Number.isFinite(n) ? n : 0;
}

function safeBool(v) {
  return v === true;
}

function isMlbbGame(gameName = "") {
  return String(gameName || "").toLowerCase().includes("mlbb");
}

/* =========================
 * Component
 * ========================= */
const ProductInfo = () => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  const [pageLoading, setPageLoading] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [serverLoading, setServerLoading] = useState(false);

  const [product, setProduct] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);

  const [categoryPublic, setCategoryPublic] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");

  const [userId, setUserId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [charName, setCharName] = useState("");
  const [recentDetails, setRecentDetails] = useState([]);

  const [playerName, setPlayerName] = useState("");
  const [bonusInfo, setBonusInfo] = useState(null);
  const [mlbbDetails, setMlbbDetails] = useState(null);
  const [hasChecked, setHasChecked] = useState(false);

  const [serverOptions, setServerOptions] = useState([]);
  const [serverMode, setServerMode] = useState("");
  const [serverRequiredFromApi, setServerRequiredFromApi] = useState(false);

  const [paymentMode, setPaymentMode] = useState("upi");
  const [showConfirm, setShowConfirm] = useState(false);
  const [buyNowFlash, setBuyNowFlash] = useState(false);

  const buyNowRef = useRef(null);
  const buyNowFlashTimerRef = useRef(null);

  useEffect(() => {
    getUserData(dispatch);
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (buyNowFlashTimerRef.current) {
        clearTimeout(buyNowFlashTimerRef.current);
      }
    };
  }, []);

  const productLogo = useMemo(() => assetUrl(product?.image), [product]);
  const heroImg = useMemo(
    () => assetUrl(product?.banner_image || product?.topBanner || ""),
    [product]
  );

  const gameName = String(product?.gameName || "").trim();
  const userBalance = useMemo(() => toMoneyNumber(user?.balance), [user]);
  const isReseller = user?.reseller === "yes";
  const recentDetailsKey = useMemo(() => {
    const owner = String(user?.email || "guest").trim().toLowerCase();
    const productKey = String(product?._id || product?.gameName || product?.name || params.name || "")
      .trim()
      .toLowerCase();

    if (!productKey) return "";
    return `limpook:recent-details:${owner}:${productKey}`;
  }, [params.name, product, user?.email]);

  const requiresUserId = safeBool(selectedPack?.requiresUserId);
  const requiresServerFromPack = safeBool(selectedPack?.requiresServerId);
  const requiresCharName = safeBool(selectedPack?.requiresCharName);

  const effectiveRequiresServer =
    requiresServerFromPack || safeBool(serverRequiredFromApi);

  const requiresValidation =
    requiresUserId || effectiveRequiresServer || requiresCharName;

  const useServerSelect =
    effectiveRequiresServer &&
    String(serverMode || "").toLowerCase() === "select" &&
    Array.isArray(serverOptions) &&
    serverOptions.length > 0;

  const getPackPrice = (pack) => {
    const normal = toMoneyNumber(pack?.price);
    const reseller = toMoneyNumber(pack?.reseller_price);
    return isReseller && reseller > 0 ? reseller : normal;
  };

  const finalPackPrice = useMemo(
    () => getPackPrice(selectedPack),
    [selectedPack, isReseller]
  );

  const packCategories = useMemo(() => {
    const arr = Array.isArray(product?.cost) ? product.cost : [];
    const set = new Set();
    for (const c of arr) {
      const key = String(c?.p_category || "").trim();
      if (key) set.add(key);
    }
    return Array.from(set);
  }, [product]);

  const visiblePacks = useMemo(() => {
    const packs = Array.isArray(product?.cost) ? product.cost : [];
    const key = String(activeCategory || "").trim();
    if (!key) return packs;
    return packs.filter((p) => String(p?.p_category || "").trim() === key);
  }, [product, activeCategory]);

  const hasDetailFields = requiresUserId || effectiveRequiresServer || requiresCharName;

  const loadRecentDetails = () => {
    if (!recentDetailsKey) {
      setRecentDetails([]);
      return;
    }

    try {
      const raw = localStorage.getItem(recentDetailsKey);
      const list = JSON.parse(raw || "[]");
      setRecentDetails(Array.isArray(list) ? list.slice(0, RECENT_DETAILS_LIMIT) : []);
    } catch {
      setRecentDetails([]);
    }
  };

  const resetValidationResult = () => {
    setHasChecked(false);
    setPlayerName("");
    setBonusInfo(null);
    setMlbbDetails(null);
    setShowConfirm(false);
  };

  const saveRecentDetail = (labelValue = "") => {
    if (!recentDetailsKey || !hasDetailFields) return;

    const nextDetail = {
      userId: String(userId || "").trim(),
      zoneId: String(zoneId || "").trim(),
      charName: String(charName || "").trim(),
      label: String(labelValue || playerName || charName || userId || "").trim(),
      savedAt: Date.now(),
    };

    if (!nextDetail.userId && !nextDetail.zoneId && !nextDetail.charName) return;

    try {
      const raw = localStorage.getItem(recentDetailsKey);
      const list = Array.isArray(JSON.parse(raw || "[]")) ? JSON.parse(raw || "[]") : [];
      const same = (item) =>
        String(item?.userId || "") === nextDetail.userId &&
        String(item?.zoneId || "") === nextDetail.zoneId &&
        String(item?.charName || "") === nextDetail.charName;

      const updated = [
        nextDetail,
        ...list.filter((item) => !same(item)),
      ].slice(0, RECENT_DETAILS_LIMIT);

      localStorage.setItem(recentDetailsKey, JSON.stringify(updated));
      setRecentDetails(updated);
    } catch {
      setRecentDetails((prev) => {
        const updated = [
          nextDetail,
          ...prev.filter(
            (item) =>
              String(item?.userId || "") !== nextDetail.userId ||
              String(item?.zoneId || "") !== nextDetail.zoneId ||
              String(item?.charName || "") !== nextDetail.charName
          ),
        ].slice(0, RECENT_DETAILS_LIMIT);

        return updated;
      });
    }
  };

  const applyRecentDetail = (detail) => {
    setUserId(String(detail?.userId || ""));
    setZoneId(String(detail?.zoneId || ""));
    setCharName(String(detail?.charName || ""));
    resetValidationResult();
  };

  const loadPublicCategories = async () => {
    try {
      const res = await axios.get("/api/admin/get-product-category-public");
      if (res?.data?.success) {
        setCategoryPublic(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setCategoryPublic([]);
      }
    } catch {
      setCategoryPublic([]);
    }
  };

  useEffect(() => {
    loadPublicCategories();
  }, []);

  useEffect(() => {
    loadRecentDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentDetailsKey]);

  const categoryImg = (catKey) => {
    const key = String(catKey || "").trim().toLowerCase();
    if (!key) return "";

    const found = categoryPublic.find((x) => {
      const n = String(x?.name || x?.category || x?.key || "")
        .trim()
        .toLowerCase();
      return n === key;
    });

    let img = found?.image || found?.img || found?.imageUrl || "";
    img = String(img || "").trim();

    if (img && !img.includes("/") && !img.includes("\\") && !img.includes(":/")) {
      img = `/categoryImages/${img}`;
    }

    return assetUrl(img);
  };

  const loadServerOptions = async (gamecode) => {
    const code = String(gamecode || "").trim();

    setServerLoading(true);
    setServerOptions([]);
    setServerMode("");
    setServerRequiredFromApi(false);

    if (!code) {
      setServerLoading(false);
      return;
    }

    try {
      const res = await axios.post("/api/server-options", {
        gamecode: code,
      });

      if (res?.data?.success) {
        setServerRequiredFromApi(res.data?.requiresServerId === true);
        setServerMode(String(res.data?.serverMode || "").trim());
        setServerOptions(Array.isArray(res.data?.servers) ? res.data.servers : []);
      } else {
        setServerRequiredFromApi(false);
        setServerMode("");
        setServerOptions([]);
      }
    } catch {
      setServerRequiredFromApi(false);
      setServerMode("");
      setServerOptions([]);
    } finally {
      setServerLoading(false);
    }
  };

  const getProduct = async () => {
    try {
      setPageLoading(true);

      const res = await axios.post("/api/product/get-product-by-name", {
        name: params.name,
      });

      if (!res.data?.success) {
        message.error(res.data?.message || "Failed to load product");
        return;
      }

      const p = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
      setProduct(p || null);
      if (p) saveRecentProduct(p);

      const firstPack = Array.isArray(p?.cost) ? p.cost[0] : null;
      setSelectedPack(firstPack || null);

      const firstCat = String(firstPack?.p_category || "").trim();
      setActiveCategory(firstCat);

      setPlayerName("");
      setBonusInfo(null);
      setMlbbDetails(null);
      setHasChecked(false);
      setShowConfirm(false);
      setPaymentMode("upi");
      setUserId("");
      setZoneId("");
      setCharName("");
      setBuyNowFlash(false);
    } catch (err) {
      console.log(err);
      message.error("Failed to load product");
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    getProduct();
    // eslint-disable-next-line
  }, [params.name]);

  useEffect(() => {
    if (product?.gameName) {
      loadServerOptions(product.gameName);
    }
  }, [product?.gameName]);

  useEffect(() => {
    setHasChecked(false);
    setPlayerName("");
    setBonusInfo(null);
    setMlbbDetails(null);
    setShowConfirm(false);
  }, [userId, zoneId, charName]);

  useEffect(() => {
    if (!showConfirm) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [showConfirm]);

  const scrollToBuyNow = () => {
    setTimeout(() => {
      buyNowRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      setBuyNowFlash(true);

      if (buyNowFlashTimerRef.current) {
        clearTimeout(buyNowFlashTimerRef.current);
      }

      buyNowFlashTimerRef.current = setTimeout(() => {
        setBuyNowFlash(false);
      }, 1600);
    }, 120);
  };

  const onSelectCategory = (catKey) => {
    const key = String(catKey || "").trim();
    setActiveCategory(key);

    const packs = Array.isArray(product?.cost) ? product.cost : [];
    const first =
      packs.find((p) => String(p?.p_category || "").trim() === key) || null;
    setSelectedPack(first);

    setShowConfirm(false);
  };

  const onSelectPack = (pack) => {
    setSelectedPack(pack);
    setShowConfirm(false);
    scrollToBuyNow();
  };

  const handleCheckName = async () => {
    if (!product?.gameName) {
      message.error("Invalid game");
      return;
    }

    if (requiresUserId && !String(userId || "").trim()) {
      message.error("Enter User ID");
      return;
    }

    if (effectiveRequiresServer && !String(zoneId || "").trim()) {
      message.error("Select / Enter Server");
      return;
    }

    if (requiresCharName && !String(charName || "").trim()) {
      message.error("Enter Character Name");
      return;
    }

    if (!requiresValidation) {
      setPlayerName("Verified");
      setHasChecked(true);
      setMlbbDetails(null);
      saveRecentDetail("Verified");
      return;
    }

    try {
      setCheckingName(true);
      setMlbbDetails(null);

      const params = {
        code: String(product.gameName).trim(),
        characterId: String(userId || "").trim(),
      };

      if (effectiveRequiresServer && String(zoneId || "").trim()) {
        params.server_code = String(zoneId).trim();
      }

      const res = await axios.get("/api/check/game", { params });

      if (!res.data?.success) {
        setPlayerName("");
        setBonusInfo(null);
        setMlbbDetails(null);
        setHasChecked(false);
        message.error(res.data?.message || "Name check failed");
        return;
      }

      const verifiedName =
        res.data?.username ||
        res.data?.name ||
        res.data?.data?.username ||
        res.data?.data?.name ||
        res.data?.message ||
        "Verified";

      setPlayerName(String(verifiedName || "Verified"));
      setHasChecked(true);
      saveRecentDetail(verifiedName);

      if (
        isMlbbGame(product.gameName) &&
        String(userId || "").trim() &&
        String(zoneId || "").trim()
      ) {
        try {
          const bonusRes = await axios.get("/api/check/mlbb-bonus", {
            params: {
              user_id: String(userId).trim(),
              server_id: String(zoneId).trim(),
            },
          });

          if (bonusRes?.data?.success) {
            setBonusInfo(bonusRes.data);

            setMlbbDetails({
              success: true,
              valid: res.data?.valid || "valid",
              name: res.data?.name || verifiedName || "-",
              region: res.data?.region || "-",
              user_id: bonusRes.data?.user_id || String(userId).trim(),
              username: bonusRes.data?.username || res.data?.name || verifiedName || "-",
              bonus_limit: Array.isArray(bonusRes.data?.bonus_limit)
                ? bonusRes.data.bonus_limit
                : [],
            });
          } else {
            setBonusInfo(null);
            setMlbbDetails({
              success: true,
              valid: res.data?.valid || "valid",
              name: res.data?.name || verifiedName || "-",
              region: res.data?.region || "-",
              user_id: String(userId).trim(),
              username: res.data?.name || verifiedName || "-",
              bonus_limit: [],
            });
          }
        } catch {
          setBonusInfo(null);
          setMlbbDetails({
            success: true,
            valid: res.data?.valid || "valid",
            name: res.data?.name || verifiedName || "-",
            region: res.data?.region || "-",
            user_id: String(userId).trim(),
            username: res.data?.name || verifiedName || "-",
            bonus_limit: [],
          });
        }
      } else {
        setBonusInfo(null);
        setMlbbDetails(null);
      }
    } catch (err) {
      console.log(err);
      setPlayerName("");
      setBonusInfo(null);
      setMlbbDetails(null);
      setHasChecked(false);
      message.error(err?.response?.data?.message || "Name check failed");
    } finally {
      setCheckingName(false);
    }
  };

  const validateBeforeConfirm = () => {
    if (!user) {
      navigate("/login");
      return false;
    }

    if (user?.block === "yes") {
      message.error("Your account is blocked");
      return false;
    }

    if (product?.stock === "no") {
      message.error("Out of stock");
      return false;
    }

    if (!selectedPack?.packid) {
      message.error("Please select a pack");
      return false;
    }

    if (requiresUserId && !String(userId || "").trim()) {
      message.error("Enter User ID");
      return false;
    }

    if (effectiveRequiresServer && !String(zoneId || "").trim()) {
      message.error("Select / Enter Server");
      return false;
    }

    if (requiresCharName && !String(charName || "").trim()) {
      message.error("Enter Character Name");
      return false;
    }

    if (requiresValidation && !hasChecked) {
      message.error("Please validate first");
      return false;
    }

    return true;
  };

  const handleOpenConfirm = () => {
    if (!validateBeforeConfirm()) return;
    setShowConfirm(true);
  };

  const handleConfirmOrder = async () => {
    if (!validateBeforeConfirm()) return;

    try {
      setPlacingOrder(true);

      const mode = String(paymentMode || "upi").toLowerCase();

      if (mode === "wallet") {
        if (finalPackPrice <= 0) {
          message.error("Invalid pack price");
          return;
        }

        if (userBalance < finalPackPrice) {
          message.error("Insufficient wallet balance");
          return;
        }
      }

      const payload = {
        product_name: product?.name,
        product_gameName: product?.gameName,
        packid: selectedPack?.packid,
        amount: selectedPack?.amount,
        userid: String(userId || "").trim(),
        zoneid: String(zoneId || "").trim(),
        payment_mode: mode,
      };

      if (requiresCharName) {
        payload.char_name = String(charName || "").trim();
      }

      const res = await axios.post(ORDER_CREATE_ENDPOINT, payload, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });

      if (res.data?.success) {
        saveRecentDetail(playerName);

        if (mode === "upi") {
          const payUrl =
            res.data?.payment_url ||
            res.data?.redirect_url ||
            res.data?.redirect ||
            res.data?.data?.payment_url ||
            res.data?.data?.result?.payment_url;

          if (payUrl) {
            message.success("Redirecting to payment...");
            window.location.href = payUrl;
            return;
          }

          message.success(res.data?.message || "Order created");
          setShowConfirm(false);
          navigate("/user-dashboard");
          return;
        }

        message.success(res.data?.message || "Order placed using wallet");
        setShowConfirm(false);

        try {
          await getUserData(dispatch);
        } catch {}

        navigate("/user-dashboard");
        return;
      }

      message.error(res.data?.message || "Failed to place order");
    } catch (err) {
      console.log(err);
      message.error(err?.response?.data?.message || "Failed to place order");
    } finally {
      setPlacingOrder(false);
    }
  };

  const showLoader = pageLoading || !product;

  return (
    <Layout>
      {showLoader ? (
        <div className="piLoadingWrap">
          <div className="piSpinner" />
        </div>
      ) : (
        <div className="piWrap">
          <section className="product-hero-section">
            <div className="hero-visual">
              {heroImg ? (
                <img
                  className="hero-banner"
                  src={heroImg}
                  alt={product?.name || "Product banner"}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="hero-banner-fallback" />
              )}

              {productLogo && (
                <div className="hero-logo-wrap">
                  <img
                    className="hero-logo"
                    src={productLogo}
                    alt={product?.name || "Product"}
                    onError={(e) => {
                      e.currentTarget.src = "/logo192.png";
                    }}
                  />
                </div>
              )}
            </div>

            <div className="hero-content">
              <h1 className="hero-title">{product?.name || "Top-up"}</h1>

              <ul className="hero-tags">
                <li>Instant Delivery</li>
                <li>24/7 Support</li>
                <li>Secure Checkout</li>
              </ul>
            </div>
          </section>

          <div className="piRight">
            {packCategories.length > 0 && (
              <div className="piCard piSection piCategorySection">
                <div className="piCardHead">
                  <h5>Select Category</h5>
                </div>

                <div className="piCatStrip">
                  {packCategories.map((cat) => {
                    const active = String(activeCategory) === String(cat);
                    const img = categoryImg(cat);

                    return (
                      <button
                        type="button"
                        key={cat}
                        className={`piCatBtn ${active ? "active" : ""}`}
                        onClick={() => onSelectCategory(cat)}
                        title={prettyCat(cat)}
                        aria-label={prettyCat(cat)}
                      >
                        <div className="piCatImg">
                          {img ? (
                            <img
                              src={img}
                              alt={cat}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <span className="piCatFallback">
                              {prettyCat(cat).slice(0, 1)}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="piCard piSection piDetailsSection">
              <div className="piCardHead">
                <h5>Enter Details</h5>
              </div>

              <div className="piForm">
                {hasDetailFields && recentDetails.length > 0 && (
                  <div className="piRecentDetails">
                    <div className="piRecentHead">
                      <span>Recent Details</span>
                      <small>Tap to fill</small>
                    </div>

                    <div className="piRecentList">
                      {recentDetails.map((detail, idx) => {
                        const meta = [
                          requiresUserId && detail?.userId ? `ID: ${detail.userId}` : "",
                          effectiveRequiresServer && detail?.zoneId ? `Server: ${detail.zoneId}` : "",
                          requiresCharName && detail?.charName ? `Name: ${detail.charName}` : "",
                        ].filter(Boolean).join(" | ");

                        return (
                          <button
                            type="button"
                            className="piRecentItem"
                            key={`${detail?.userId || "user"}-${detail?.zoneId || "zone"}-${idx}`}
                            onClick={() => applyRecentDetail(detail)}
                          >
                            <strong>{detail?.label || detail?.userId || "Saved ID"}</strong>
                            <span>{meta || "Saved for this product"}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {requiresUserId && (
                  <input
                    className="piInput"
                    type="text"
                    placeholder="User ID"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                )}

                {effectiveRequiresServer && (
                  <>
                    {serverLoading ? (
                      <div className="piHint">Loading server options...</div>
                    ) : useServerSelect ? (
                      <select
                        className="piInput"
                        value={zoneId}
                        onChange={(e) => setZoneId(e.target.value)}
                      >
                        <option value="">Select Server</option>
                        {serverOptions.map((srv, idx) => (
                          <option
                            key={srv?.value || idx}
                            value={String(srv?.value || "").trim()}
                          >
                            {srv?.label || srv?.value || "Server"}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="piInput"
                        type="text"
                        placeholder="Server / Zone ID"
                        value={zoneId}
                        onChange={(e) => setZoneId(e.target.value)}
                      />
                    )}
                  </>
                )}

                {requiresCharName && (
                  <input
                    className="piInput"
                    type="text"
                    placeholder="Character Name"
                    value={charName}
                    onChange={(e) => setCharName(e.target.value)}
                  />
                )}

                <button
                  className="piBtn"
                  onClick={handleCheckName}
                  disabled={checkingName || !selectedPack?.packid}
                >
                  {checkingName ? "Checking..." : "Validate"}
                </button>

                {isMlbbGame(product?.gameName) && mlbbDetails?.success ? (
                  <div className="piCheckCard">
                    <div className="piCheckTitle">MLBB Account Details</div>

                    <div className="piCheckRow">
                      <span>Name</span>
                      <strong>{mlbbDetails?.name || "-"}</strong>
                    </div>

                    <div className="piCheckRow">
                      <span>Status</span>
                      <strong>{mlbbDetails?.valid || "-"}</strong>
                    </div>

                    <div className="piCheckRow">
                      <span>Region</span>
                      <strong>{mlbbDetails?.region || "-"}</strong>
                    </div>

                    <div className="piCheckRow">
                      <span>User ID</span>
                      <strong>{mlbbDetails?.user_id || "-"}</strong>
                    </div>

                    <div className="piCheckRow">
                      <span>Username</span>
                      <strong>{mlbbDetails?.username || "-"}</strong>
                    </div>

                    {Array.isArray(mlbbDetails?.bonus_limit) &&
                      mlbbDetails.bonus_limit.length > 0 && (
                        <div className="piBonusList">
                          {mlbbDetails.bonus_limit.map((item, idx) => (
                            <div
                              className="piBonusItem"
                              key={`${item?.title || "bonus"}-${idx}`}
                            >
                              <span>{item?.title || "-"}</span>
                              <strong className={item?.reached_limit ? "isUsed" : "isOpen"}>
                                {item?.reached_limit ? "Used" : "Available"}
                              </strong>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ) : playerName ? (
                  <div className="piOk">Username: {playerName}</div>
                ) : null}
              </div>
            </div>

            <div className="piCard piSection piPackagesSection">
              <div className="piCardHead">
                <h5>Select Package</h5>
              </div>

              <div className="piPacks">
                {visiblePacks?.map((item, index) => {
                  const active =
                    String(item?.packid) === String(selectedPack?.packid);

                  return (
                    <button
                      type="button"
                      key={item?.packid || index}
                      onClick={() => onSelectPack(item)}
                      className={`piPack ${active ? "active" : ""}`}
                    >
                      <div className="piPackTop">
                        <div className="piPackName">{item?.amount}</div>
                        <img
                          className="piPackIcon"
                          src={assetUrl(item?.pimg)}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>

                      <div className="piPackPrice">₹{getPackPrice(item)}</div>
                    </button>
                  );
                })}
              </div>

              {!selectedPack && (
                <small className="piHint">Please select a package first.</small>
              )}
            </div>

            <div className="piCard piSection piDescriptionSection">
              <div className="piCardHead">
                <h5>Description</h5>
              </div>

              <p className="piDescText">
                {product?.desc || "Choose your package, enter the required account details, validate, and complete your order securely."}
              </p>
            </div>

            <div
              ref={buyNowRef}
              className={`piCard piSection piBuyCard ${buyNowFlash ? "piBuyCardFlash" : ""}`}
            >
              <div className="piBuyRow">
                <div>
                  <h5>Buy Now</h5>
                  <div className="piSmall">Pack: {selectedPack?.amount || "-"}</div>

                  {!!user && (
                    <div className="piSmall" style={{ marginTop: 6 }}>
                      Wallet Balance: ₹{userBalance}
                    </div>
                  )}
                </div>

                <div className="piTotal">₹{finalPackPrice}</div>
              </div>

              <div className="piBuyBtnWrap">
                {product?.stock === "no" ? (
                  <button className="piBtn" disabled>
                    Out of Stock
                  </button>
                ) : !user ? (
                  <button onClick={() => navigate("/login")} className="piBtn">
                    Please Login First
                  </button>
                ) : user?.block === "yes" ? (
                  <button className="piBtn" disabled>
                    Account Blocked
                  </button>
                ) : requiresValidation && !hasChecked ? (
                  <button className="piBtn" disabled>
                    Please Validate First
                  </button>
                ) : (
                  <button
                    onClick={handleOpenConfirm}
                    className="piBtn"
                    disabled={placingOrder}
                  >
                    {placingOrder ? "Processing..." : "BUY NOW"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {showConfirm && (
            <div
              className="piSheetBackdrop"
              onClick={() => {
                if (!placingOrder) setShowConfirm(false);
              }}
            >
              <div
                className="piSheet"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <div className="piSheetHandle" />

                <div className="piSheetHead">
                  <div>
                    <div className="piSheetTitle">Confirm Order</div>
                    <div className="piSheetSub">
                      Please verify details before ordering
                    </div>
                  </div>

                  <button
                    className="piSheetClose"
                    onClick={() => !placingOrder && setShowConfirm(false)}
                    disabled={placingOrder}
                    aria-label="Close"
                    type="button"
                  >
                    X
                  </button>
                </div>

                <div className="piSheetBody">
                  <div className="piSheetRow piPayRow">
                    <span className="k">Payment</span>

                    <div className="piPayRight">
                      <select
                        className="piPaySelect"
                        value={paymentMode}
                        onChange={(e) => setPaymentMode(e.target.value)}
                        disabled={placingOrder}
                      >
                        <option value="upi">UPI</option>
                        <option value="wallet">Wallet</option>
                      </select>

                      {paymentMode === "wallet" ? (
                        <span className="piPayHint">Wallet: ₹{userBalance}</span>
                      ) : (
                        <span className="piPayHint">UPI Redirect</span>
                      )}
                    </div>
                  </div>

                  {paymentMode === "wallet" && (
                    <>
                      <div className="piSheetRow">
                        <span className="k">Your Wallet</span>
                        <span className="v">₹{userBalance}</span>
                      </div>
                      <div className="piSheetRow">
                        <span className="k">After Purchase</span>
                        <span className="v">
                          ₹{Math.max(0, userBalance - finalPackPrice)}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="piSheetDivider" />

                  {requiresUserId && (
                    <div className="piSheetRow">
                      <span className="k">User ID</span>
                      <span className="v">{userId || "-"}</span>
                    </div>
                  )}

                  {effectiveRequiresServer && (
                    <div className="piSheetRow">
                      <span className="k">Server / Zone</span>
                      <span className="v">{zoneId || "-"}</span>
                    </div>
                  )}

                  {requiresCharName && (
                    <div className="piSheetRow">
                      <span className="k">Character Name</span>
                      <span className="v">{charName || "-"}</span>
                    </div>
                  )}

                  {requiresValidation && (
                    <div className="piSheetRow">
                      <span className="k">Player</span>
                      <span className="v">{playerName || "-"}</span>
                    </div>
                  )}

                  <div className="piSheetDivider" />

                  <div className="piSheetRow">
                    <span className="k">Pack</span>
                    <span className="v">{selectedPack?.amount || "-"}</span>
                  </div>

                  <div className="piSheetRow">
                    <span className="k">Amount</span>
                    <span className="v price">₹{finalPackPrice}</span>
                  </div>

                  {paymentMode === "wallet" && userBalance < finalPackPrice ? (
                    <div className="piOk" style={{ marginTop: 10, color: "#b86d6d" }}>
                      Insufficient wallet balance.
                    </div>
                  ) : null}
                </div>

                <div className="piSheetFooter">
                  <button
                    type="button"
                    className="piBtn piBtnGhost"
                    onClick={() => !placingOrder && setShowConfirm(false)}
                    disabled={placingOrder}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="piBtn piBtnPrimary"
                    onClick={handleConfirmOrder}
                    disabled={
                      placingOrder ||
                      (String(paymentMode) === "wallet" &&
                        userBalance < finalPackPrice)
                    }
                  >
                    {placingOrder ? "Ordering..." : "ORDER NOW"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
};

export default ProductInfo;
